/**
 * Feature Flag System
 *
 * Centralized feature flag infrastructure for gradual rollout of new functionality.
 * All new features in the platform rebuild should be gated behind these flags.
 *
 * Storage: user-scoped, DB-backed (public.user_feature_flags). Flags follow the
 * user across devices and sessions. No localStorage / sessionStorage is used.
 *
 * Resolution order:
 *   1. DB override row exists → use row.enabled
 *   2. No row → use FLAG_CONFIGS[flag].defaultEnabled
 *   3. No config entry → false (with a console.warn)
 */

import { useState, useEffect } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { getSupabaseClient } from './supabaseClient';

// Feature flag keys
// Dead flags (never wired to any code) were pruned in favor of a minimal
// registry. See migration 344_prune_dead_flags.sql. If you add a flag here,
// add a matching entry to FLAG_CONFIGS below and — once wired — to
// FEATURE_FLAG_USAGE so the admin page can reflect where it's consumed.
export const FEATURE_FLAGS = {
  // Workstream 2: Evidence System
  EVIDENCE_ENFORCEMENT: 'evidence_enforcement', // Wired: valueProposalStore

  // Workstream 4: Cycle Boundary Clarity
  CYCLE_READINESS: 'cycle_readiness_validation',

  // Workstream 5: Strategy Chain
  STRATEGY_NAV: 'strategy_nav',
  STRATEGY_KERNEL_NAV: 'strategy_kernel_nav',
  PLANNING_NAV: 'planning_nav',
  PLAN_DRIFT: 'plan_drift_detection',

  // Workstream 6: Workflow Enforcement
  GOOD_ITEM_TEST: 'good_item_test',
  BACKLOG_HEALTH: 'backlog_health',

  // Item metadata
  SUBJECT_TYPES: 'subject_types',

  // Design Hub
  DESIGN_HUB: 'design_hub',

  // Contributor Role Navigation (pairs with contributor_role_nav workspace module)
  // When on: non-admins get the job-role-shaped contributor sidebar.
  // When off: everyone sees the full operating-domain sidebar (still permission-filtered).
  CONTRIBUTOR_ROLE_NAV: 'contributor_role_nav',

  // Design-First UI Previews (wired to HTML prototype canvases)
  CYCLE_DESIGNER_V2: 'cycle_designer_v2',
  TRACK_WORKSPACE_V2: 'track_workspace_v2',

  // Event Designer — the session-design surface at
  // /prioritization/sessions/new, unconditionally (DEP-016 completed
  // 2026-08-31: the Session Creation Wizard V2 module, flag and component were
  // deleted rather than promoted). This flag only gates the additional
  // /prioritization/sessions/explore route.
  // Design spec: docs/9-exploration/session-engine/.
  // Open canon question: the Designer presents S1–S5 where canon names S1–S6.
  // That divergence is real and unresolved — it wants a canon follow-up, not a
  // silent re-demotion of the default surface.
  SESSION_ENGINE_V3: 'session_engine_v3',


  // Agents as Users — Voices (pairs with the agents_as_users workspace module)
  // Phase 1: invite Voices to async sessions as Advisory voters. Default off;
  // Backspace workspace module enabled via migration 567.
  AGENTS_AS_USERS: 'agents_as_users',

  // Story overlay (pairs with the story_overlay workspace module). The track
  // page opens with where the track is and the one thing to do now. Built for
  // sales and education workspaces (owner ruling 2026-10-20); on by default
  // like every module (ruling 2026-10-14), switched off per workspace.
  STORY_OVERLAY: 'story_overlay',

  // Voices Phase 2 — counted seats + calibration ledger (PRD §4/§7).
  // Rides the SAME agents_as_users workspace module (same surface, phase 2);
  // this flag is the additional per-user gate for counted-policy UI/behavior.
  // Default OFF everywhere: advisory remains the floor until deliberately
  // enabled. Migrations 601/602.
  VOICES_COUNTED: 'voices_counted',

  // Signal & Response v2 (pairs with the signal_response workspace module).
  // Canon 23 v2 §6; PRD docs/9-exploration/SIGNAL-RESPONSE-V2-PRD.md.
  // When ON, POR condition surfaces render the honest signal-watch band +
  // unified signal log (no A/B/C/D letters selectable anywhere). When OFF,
  // the legacy read-only condition band renders unchanged. Migrations 672/673.
  SIGNAL_RESPONSE: 'signal_response',

  // Canon-Aware Harry (pairs with the canon_aware_harry workspace module).
  // Slice 1 of the AI platform brief: layered instruction stack + surface
  // context packet for the workspace guide. Voice, never authority.
  CANON_AWARE_HARRY: 'canon_aware_harry',

  // Session Copilot (pairs with the session_copilot workspace module).
  // Slice 2 of the AI platform brief: session facilitation copilot on the
  // layered instruction stack + session context packet. Work lane only —
  // drafts are text, never actions. Voice, never authority.
  SESSION_COPILOT: 'session_copilot',
  // Exchange Drafting Aids (pairs with the exchange_drafting workspace module).
  // Slice 3 of the AI platform brief: "draft, never throw" — Loop Intent /
  // Interpretation / Proposal-annotation drafts into local form state only.
  EXCHANGE_DRAFTING: 'exchange_drafting',

  // Ambient Runtime — AMB-0 (pairs with the ambient_runtime workspace module).
  // Scheduled background jobs + delivery log; spec in
  // docs/9-exploration/AMBIENT-INTELLIGENCE-PRD.md §2.
  AMBIENT_RUNTIME: 'ambient_runtime',

  // Semantic Connectors (pairs with the semantic_connectors workspace module).
  // Excursions: round-trip meaning with external apps; spec in
  // docs/9-exploration/semantic-connectors/.
  SEMANTIC_CONNECTORS: 'semantic_connectors',

  // Product Hierarchy (pairs with the product_hierarchy workspace module).
  // A product tree as a first-class hierarchy type; spec in
  // docs/9-exploration/HIERARCHY-TYPES-ARCHITECTURE-BRIEF.md.
  PRODUCT_HIERARCHY: 'product_hierarchy',

  // Structure Reorganization (pairs with the structure_reorg workspace module).
  // Draft, map and authorize a new major structure version; spec in
  // docs/9-exploration/PRD-structure-versioning-and-transitions.md (Phase 3).
  STRUCTURE_REORG: 'structure_reorg',

  // Record Address (pairs with the record_address workspace module).
  // External, view-only share links for a ratified Plan of Record; spec in
  // docs/9-exploration/SPEC-plan-of-record-redesign.md §4 Move 2.
  RECORD_ADDRESS: 'record_address',

  // Query-fed Item Pools (pairs with the query_fed_pools workspace module).
  // Feed a pool from a saved query, diffed refresh while unlocked, severance
  // at lock, the Arrival Bay after; docs/9-exploration/LARGE-SETS-QUERY-AND-SMART-SETS-PRD.md §4.3.
  QUERY_FED_POOLS: 'query_fed_pools',

  // Pool Amendment (pairs with the pool_amendment workspace module).
  // amend_locked_pool — the only path that changes a locked pool; default OFF (D3).
  POOL_AMENDMENT: 'pool_amendment',

  // Overlay Wizard (migrated from legacy featureFlagManager).
  // Gates the floating onboarding/guidance overlay rendered by OverlayWizard.tsx.
  OVERLAY_WIZARD: 'overlay_wizard',
  WIZARD_RECOMMENDATIONS: 'wizard_recommendations',
  WIZARD_TUTORIAL: 'wizard_tutorial',
  WIZARD_CONTEXTUAL_GUIDANCE: 'wizard_contextual_guidance',

  // ── Individual flags for platform modules ────────────────────────────────
  // These pair with workspace modules of the same key. Both the module (workspace
  // admin) and the flag (individual) must be enabled for the feature to be visible.
  // All default to true so that when a workspace enables the module, members get
  // the feature by default without needing to take any action (opt-out model).
  STAKEHOLDER_PULSE: 'stakeholder_pulse',
  COST_OF_DELAY: 'cost_of_delay',
  COMMITMENT_REGISTRY: 'commitment_registry',

  // Strategy Canvas (workspace module + individual flag)
  STRATEGY_CANVAS_NAV: 'strategy_canvas_nav',

  // V2MOM (workspace module + individual flag)
  V2MOM_NAV: 'v2mom_nav',

  // Lexicon — Language Intelligence (experimental)
  // Builds NLP meta-model profiles from meeting transcripts.
  LEXICON: 'lexicon',

  // Judgment Index — Hartman formal-axiology profiles (advisory)
  JUDGMENT_INDEX: 'judgment_index',

  // Judgment Analytics — decision-intelligence / judgment-taxonomy module.
  // Pairs with the judgment_analytics workspace module + entitlement. Gates the
  // calibration, non-consensus-edge, lens-profiling and trust-map surfaces.
  // Advisory only — never alters rankings or authority state. Default off.
  JUDGMENT_ANALYTICS: 'judgment_analytics',

  // Delivery tracking — item delivery records + calibration dashboard
  DELIVERY_TRACKING: 'delivery_tracking',

  // Method Intro — first-run teaser + guided tour connecting Managing
  // Priorities (DEGAP) to the app. On by default like every module (owner
  // ruling 2026-10-14); a workspace admin can switch it off. The permanent /method
  // reference pages (phase explanations, object types, toolbox) are NOT
  // gated by this — only the teaser and /method/tour are.
  METHOD_INTRO: 'method_intro',

  // Group decisions — a small group scores options together from a shared
  // link (/groups/*). On by default like every module (owner ruling
  // 2026-10-14); a workspace admin can switch it off. Off hides every in-app
  // way to start or reach one; guest join links (/groups/join/:token) keep
  // working either way.
  GROUP_DECISIONS: 'group_decisions',

  // Decision Log — the Decision Brief surface (/brief, /brief/new, /brief/:id
  // and the /prioritization/decisions/brief bridge). On by default like every
  // module (owner ruling 2026-10-14). Its public token links (/brief/submit/:token,
  // /brief/view/:token) keep working either way. The Decisions page
  // (/prioritization/decisions) is not gated by this.
  DECISION_BRIEF: 'decision_brief',

  // Execution recipe — work-type templates surfaced on item pre-planning
  EXECUTION_RECIPE: 'execution_recipe',

  // Team pages (workspace module `elevation_platform` + this individual flag)
  // Gates the team hub (/home) and the team pages behind it, and Elevation's
  // own pages, which open for their owner only (lib/elevationAccess.ts). Both
  // the workspace module and this individual flag must be enabled.
  ELEVATION_NAV: 'elevation_nav',

  // Session Waiting Room
  // Gates the pre-session gathering space where participants check in before
  // ranking begins. When enabled: participants land in a Waiting Room phase
  // (live roster via Realtime on session_presence), and facilitators see a
  // Waiting Room tab in SessionDetail with a Signal Start button.
  // Requires migration 499_session_waiting_room.sql (session_presence table).
  SESSION_WAITING_ROOM: 'session_waiting_room',

  // ── Rating Explanation & Ranking Expression ───────────────────────────────────
  // Gates the ability for participants to attach a rationale when they rate or
  // rank an item, and to choose the lens through which they're casting their vote.
  // Sub-flags require the master switch to also be on.
  RATING_EXPLANATION: 'rating_explanation',
  RANKING_EXPRESSION_IMPORTANCE: 'ranking_expression_importance',
  RANKING_EXPRESSION_CONFIDENCE: 'ranking_expression_confidence',

  // ── Relationship Graph Extensions ────────────────────────────────────────────
  // Four independently feature-flagged power-ups for the item relationship graph.
  // All default to false (opt-in). Enable via the Feature Flags settings page.
  GRAPH_INSPECTOR: 'graph_relationship_inspector',
  GRAPH_PATH_ANALYSIS: 'graph_path_analysis',
  GRAPH_CLUSTER_RESOLUTION: 'graph_cluster_resolution',
  GRAPH_NEXUS_OVERLAY: 'graph_nexus_overlay',
  GRAPH_POOL_VIOLATIONS: 'graph_pool_violations',

  // Whiteboard import reveal — the post-commit spectacle layer (fog-lift
  // reveal, "play what happened" walkthrough, exportable diagram) plus
  // sample-board demo mode in the import dialog. Presentation only: it gates
  // what happens AFTER a whiteboard import commits, never parse/plan/commit.
  WHITEBOARD_REVEAL: 'whiteboard_reveal',
  /** Whiteboard photo import on our own Claude key (S-32). Off until the 20-photo accuracy test passes. */
  WHITEBOARD_MANAGED_KEY: 'whiteboard_managed_key',

  // Time-phased execution planning (pairs with time_phased_planning workspace module).
  // Gates Launch Calendar, supply calendar workbench, and counterfactual levers.
  TIME_PHASED_PLANNING: 'time_phased_planning',

  // Unified Plan of Record narrative (pairs with the plan_narrative workspace
  // module). Gates the plan-cycle narrative subpage + summary card and the
  // standalone-URL redirect for cycle-linked records (DEP-026).
  PLAN_NARRATIVE: 'plan_narrative',

  // Decision latency report (pairs with the decision_latency workspace module)
  DECISION_LATENCY: 'decision_latency',

  // Typed conditions on decisions and plan commitments (pairs with the
  // conditions workspace module). Gates condition capture, the watcher's
  // readouts, and the per-commitment condition-health strip. Spec:
  // docs/9-exploration/CONDITION-MONITORING-AND-DECISION-EXPIRY-PRD.md.
  CONDITIONS: 'conditions',

  // Announced-vs-committed commitment state (pairs with the commitment_states
  // workspace module). Gates the derived state readout on the above/below-line
  // view and the Plan of Record rollup, and the displacement ceremony at
  // admission. Spec: docs/9-exploration/DISPLACEMENT-AND-COMMITMENT-STATES-PRD.md.
  COMMITMENT_STATES: 'commitment_states',

  // Canonical commitment view + downstream staleness markers (pairs with the
  // commitment_view workspace module)
  COMMITMENT_VIEW: 'commitment_view',

  // The structural moment before criteria are finalized (pairs with the
  // comparison_set_validation workspace module). Gates the S3 validation step
  // that reckons with Twins identity, Nexus structure and the anti-pattern
  // report in Rule 13 order, and records what the room decided.
  COMPARISON_SET_VALIDATION: 'comparison_set_validation',

  // Qualified capacity from a roster (pairs with the resource_roster workspace
  // module). Gates the People -> Roles surface, roster-derived pools, and the
  // qualification dimensions (capability, credential, named resource) that turn
  // "no engineering capacity" into an answer a staffing conversation can use.
  // Spec: docs/9-exploration/PLANNING-RESOURCE-MODEL-AND-VIABILITY-BANDS-PRD.md.
  RESOURCE_ROSTER: 'resource_roster',

  // ── App Layer — consumer-simple apps on the foundation ────────────────────
  // One flag per app, pairing with a workspace module of the same key (build
  // contract §2.6). Each gates one /apps/<appId>/* route family rendered in
  // C Mode only. Steering spec: PRD-app-layer (App Registry: src/apps/registry.ts).
  APP_SESSION: 'app_session',
  APP_RANK: 'app_rank',
  APP_CUT: 'app_cut',
  APP_ITEMS: 'app_items',
  APP_VIABILITY: 'app_viability',
  APP_FOCUS: 'app_focus',
  APP_INTAKE: 'app_intake',
  APP_REVIEW: 'app_review',
  APP_CLASSIFICATION: 'app_classification',
  APP_RELATIONSHIPS: 'app_relationships',
  APP_ALIGN: 'app_align',
  APP_ALLOCATE: 'app_allocate',
  APP_OFFSITE: 'app_offsite',
  APP_HIRE: 'app_hire',
  APP_VENDOR_SELECT: 'app_vendor_select',
  APP_RISK_TRIAGE: 'app_risk_triage',
  APP_POST_MORTEM: 'app_post_mortem',
  APP_ASK_THE_ROOM: 'app_ask_the_room',
  APP_START: 'app_start',
  APP_SESSION_IMPORT: 'app_session_import',
  APP_DECISIONS: 'app_decisions',
  APP_MANDATE: 'app_mandate',
  APP_WHATS_NEXT: 'app_whats_next',
  APP_FIRST_100_DAYS: 'app_first_100_days',
} as const;

export type FeatureFlagKey = keyof typeof FEATURE_FLAGS;
export type FeatureFlagValue = typeof FEATURE_FLAGS[FeatureFlagKey];

// Flag configuration with metadata
export interface FeatureFlagConfig {
  key: FeatureFlagValue;
  name: string;
  description: string;
  defaultEnabled: boolean;
  workstream: 'ws1' | 'ws2' | 'ws3' | 'ws4' | 'ws5' | 'ws6' | 'design' | 'platform';
  dependencies?: FeatureFlagValue[];
  /**
   * When true, this flag controls behavioral enforcement or a UX preference —
   * not access to a feature surface. It has no corresponding workspace module
   * because workspace admins have no reason to gate the behavior system-wide.
   * These flags are intentionally individual-only.
   */
  behavioralOnly?: boolean;
  /**
   * When true, omit this flag from Settings → Feature Flags. The flag remains
   * in the registry (gates still resolve) for cleanup/retirement — see the
   * Deprecation Register entry that owns the hide.
   */
  hideFromFeatureFlagsSettings?: boolean;
}

export const FLAG_CONFIGS: Record<FeatureFlagValue, FeatureFlagConfig> = {
  // Workstream 2
  [FEATURE_FLAGS.EVIDENCE_ENFORCEMENT]: {
    key: FEATURE_FLAGS.EVIDENCE_ENFORCEMENT,
    name: 'Evidence Enforcement',
    description: 'Enforce evidence requirements when proposing attribute values (block or warn per attribute config)',
    defaultEnabled: false,
    workstream: 'ws2',
    behavioralOnly: true,
    // NOTE: The 'evidence' workspace module controls access to the Evidence feature.
    // This flag controls enforcement strictness (block vs warn). They are different axes.
  },

  // Workstream 4
  [FEATURE_FLAGS.CYCLE_READINESS]: {
    key: FEATURE_FLAGS.CYCLE_READINESS,
    name: 'Cycle Readiness Validation',
    description: 'Validate cycle setup before phase transitions',
    defaultEnabled: true, // ENABLED
    workstream: 'ws4',
    behavioralOnly: true,
  },

  // Workstream 5
  [FEATURE_FLAGS.STRATEGY_NAV]: {
    key: FEATURE_FLAGS.STRATEGY_NAV,
    name: 'Strategy Navigation',
    description: 'Show or hide the Strategy section in the main sidebar navigation.',
    defaultEnabled: true,
    workstream: 'ws5',
  },
  [FEATURE_FLAGS.STRATEGY_KERNEL_NAV]: {
    key: FEATURE_FLAGS.STRATEGY_KERNEL_NAV,
    name: 'Strategy Kernel Navigation',
    description: 'Show or hide the Strategy Kernel entry in the Strategy section of the sidebar.',
    defaultEnabled: true,
    workstream: 'ws5',
    dependencies: [FEATURE_FLAGS.STRATEGY_NAV],
  },
  [FEATURE_FLAGS.PLANNING_NAV]: {
    key: FEATURE_FLAGS.PLANNING_NAV,
    name: 'Planning Navigation',
    description: 'Show or hide the Plans of Record entry in the sidebar navigation.',
    defaultEnabled: true,
    workstream: 'ws5',
  },
  [FEATURE_FLAGS.PLAN_DRIFT]: {
    key: FEATURE_FLAGS.PLAN_DRIFT,
    name: 'Plan Drift Detection',
    description: 'Detect when plans diverge from Published Priority Lists',
    defaultEnabled: true,
    workstream: 'ws5',
  },

  // Workstream 6
  [FEATURE_FLAGS.GOOD_ITEM_TEST]: {
    key: FEATURE_FLAGS.GOOD_ITEM_TEST,
    name: 'Good Item Test',
    description: '6-check validation for item quality',
    defaultEnabled: false,
    workstream: 'ws6',
    behavioralOnly: true,
  },
  [FEATURE_FLAGS.BACKLOG_HEALTH]: {
    key: FEATURE_FLAGS.BACKLOG_HEALTH,
    name: 'Backlog Health',
    description: 'Backlog health scoring, diagnostics, and recommendations for item quality and readiness',
    defaultEnabled: true,
    workstream: 'ws6',
  },

  // Item metadata
  [FEATURE_FLAGS.SUBJECT_TYPES]: {
    key: FEATURE_FLAGS.SUBJECT_TYPES,
    name: 'Subject Types',
    description: 'Show the Subject Type field on item detail and item work pages for tagging items with a meta-category (person, technology, process, etc.)',
    defaultEnabled: false,
    workstream: 'ws6',
    behavioralOnly: true,
  },

  // Design Hub
  [FEATURE_FLAGS.DESIGN_HUB]: {
    key: FEATURE_FLAGS.DESIGN_HUB,
    name: 'Design Hub',
    description: 'Show the Design Hub nav item in the Processes section of the sidebar.',
    defaultEnabled: true,
    workstream: 'ws6',
  },

  [FEATURE_FLAGS.CONTRIBUTOR_ROLE_NAV]: {
    key: FEATURE_FLAGS.CONTRIBUTOR_ROLE_NAV,
    name: 'Role-based member navigation',
    description:
      'When enabled (and the Role-based member navigation workspace module is on), non-admin users see the job-role-shaped contributor sidebar (MY WORK · OUTPUTS · WORKSPACE). When disabled, you see the full operating-domain sidebar. This does not hide apps — it only changes navigation shape.',
    defaultEnabled: true,
    workstream: 'platform',
  },

  // ============================================
  // DESIGN-FIRST UI PREVIEWS
  // ============================================
  [FEATURE_FLAGS.CYCLE_DESIGNER_V2]: {
    key: FEATURE_FLAGS.CYCLE_DESIGNER_V2,
    name: 'Cycle Designer v2',
    description: 'When enabled, new cycles open the React Cycle Designer wizard (4-step path builder) instead of the setup wizard or Cycle Detail iframe.',
    defaultEnabled: true,
    workstream: 'design',
  },
  [FEATURE_FLAGS.TRACK_WORKSPACE_V2]: {
    key: FEATURE_FLAGS.TRACK_WORKSPACE_V2,
    name: 'Track Workspace V2',
    description:
      'When enabled, opening a Track uses the new workspace design — a conditions-met layout with a sidebar navigator — instead of the traditional tabbed detail page.',
    defaultEnabled: true,
    workstream: 'design',
  },
  [FEATURE_FLAGS.SESSION_ENGINE_V3]: {
    key: FEATURE_FLAGS.SESSION_ENGINE_V3,
    name: 'Event Designer on /explore',
    description:
      'The Event Designer is the session surface at /prioritization/sessions/new, unconditionally. This flag only adds the extra /prioritization/sessions/explore route; when OFF, /explore redirects to the sessions list.',
    defaultEnabled: false,
    workstream: 'design',
  },
  [FEATURE_FLAGS.AGENTS_AS_USERS]: {
    key: FEATURE_FLAGS.AGENTS_AS_USERS,
    name: 'Agents as Users (Voices)',
    description:
      'When enabled (and the Agents as Users workspace module is on), Voices appear in Settings and async session participant pickers as context providers (commentary + citations, not votes). Voice, never authority.',
    defaultEnabled: true,
    workstream: 'design',
  },
  [FEATURE_FLAGS.STORY_OVERLAY]: {
    key: FEATURE_FLAGS.STORY_OVERLAY,
    name: 'Story overlay',
    description:
      'When enabled (and the Story overlay workspace module is on), a track page opens with where the track is in the work and the one thing to do now, with the control that does it. When disabled, the track page shows its usual header.',
    defaultEnabled: true,
    workstream: 'design',
  },
  [FEATURE_FLAGS.VOICES_COUNTED]: {
    key: FEATURE_FLAGS.VOICES_COUNTED,
    name: 'Voices: Counted Seats (Phase 2)',
    description:
      'When enabled (and the Agents as Users workspace module is on), facilitators can opt individual Voice seats into the Counted policy per session — counted estimator output joins the counted aggregate, always badged, and accumulates a calibration ledger. Advisory remains the default for every seat. Publishing and ratification stay human.',
    defaultEnabled: false,
    workstream: 'design',
    dependencies: [FEATURE_FLAGS.AGENTS_AS_USERS],
  },
  [FEATURE_FLAGS.SIGNAL_RESPONSE]: {
    key: FEATURE_FLAGS.SIGNAL_RESPONSE,
    name: 'Signal & Response',
    description:
      'When enabled (and the Signal & Response workspace module is on), POR condition surfaces render the Signal Framework v2 watch band and unified signal log — capture chips, classify, confirm, dismiss with rationale, derived exposure. No condition letter is selectable anywhere. The signal data layer records regardless of this flag.',
    defaultEnabled: true,
    workstream: 'design',
    dependencies: [],
  },
  [FEATURE_FLAGS.CANON_AWARE_HARRY]: {
    key: FEATURE_FLAGS.CANON_AWARE_HARRY,
    name: 'Canon-Aware Harry',
    description:
      'When enabled (and the Canon-Aware Harry workspace module is on), the workspace guide answers from the layered instruction stack (canon law digest + platform charter + agent card) with a surface context packet, instead of the legacy flat prompt.',
    defaultEnabled: true,
    workstream: 'design',
  },
  [FEATURE_FLAGS.SESSION_COPILOT]: {
    key: FEATURE_FLAGS.SESSION_COPILOT,
    name: 'Session Copilot',
    description:
      'When enabled (and the Session Copilot workspace module is on), session pages show the facilitation copilot panel: a deterministic state/stage line plus packet-grounded answers and copyable text drafts. Work lane only — the copilot never acts, scores, or ranks.',
    defaultEnabled: true,
    workstream: 'design',
  },
  [FEATURE_FLAGS.EXCHANGE_DRAFTING]: {
    key: FEATURE_FLAGS.EXCHANGE_DRAFTING,
    name: 'Exchange Drafting Aids',
    description:
      'When enabled (and the Exchange Drafting workspace module is on), Catchball composers offer "Draft with AI": Loop Intent, Interpretation, and Proposal-annotation drafts that fill the local form for the ball-holder to edit and send. The AI never moves the ball. When disabled, the surfaces are unchanged.',
    defaultEnabled: true,
    workstream: 'design',
  },
  [FEATURE_FLAGS.AMBIENT_RUNTIME]: {
    key: FEATURE_FLAGS.AMBIENT_RUNTIME,
    name: 'Ambient Runtime',
    description:
      'When enabled (and the Ambient Runtime workspace module is on), Settings → Ambient Runtime exposes background jobs, run history, the delivery log, and the workspace channel policy. Scheduled job execution is gated server-side by the workspace module, not by this personal flag.',
    defaultEnabled: true,
    workstream: 'design',
  },
  [FEATURE_FLAGS.SEMANTIC_CONNECTORS]: {
    key: FEATURE_FLAGS.SEMANTIC_CONNECTORS,
    name: 'Semantic Connectors (Excursions)',
    description:
      'When enabled (and the Semantic Connectors workspace module is on), Settings → Excursions exposes the depart/return round-trip flows for external boards (Miro first). When disabled, the surface is hidden; excursion records and applied changes are unaffected.',
    defaultEnabled: true,
    workstream: 'design',
  },
  [FEATURE_FLAGS.RECORD_ADDRESS]: {
    key: FEATURE_FLAGS.RECORD_ADDRESS,
    name: 'Record Address (share links)',
    description:
      'When enabled (and the Record Address workspace module is on), the Ratified Record page can mint and withdraw scoped, view-only share links so people outside the workspace can read a ratified Plan of Record at its address. When disabled, the panel shows the address only; existing links keep working until withdrawn.',
    defaultEnabled: true,
    workstream: 'design',
  },
  [FEATURE_FLAGS.QUERY_FED_POOLS]: {
    key: FEATURE_FLAGS.QUERY_FED_POOLS,
    name: 'Query-fed Item Pools',
    description:
      'When enabled (and the query_fed_pools workspace module is on), a pool can be fed from a saved query: an explicit, diffed "Refresh from feed" while the pool is open, severance at lock, and an Arrival Bay for items that match after lock. When disabled, the feed panel is hidden; feed bindings and arrivals are unaffected.',
    defaultEnabled: true,
    workstream: 'design',
  },
  [FEATURE_FLAGS.POOL_AMENDMENT]: {
    key: FEATURE_FLAGS.POOL_AMENDMENT,
    name: 'Pool Amendment (mid-cycle)',
    description:
      'When enabled (and the pool_amendment workspace module is on), the Arrival Bay offers "Amend into pool", which appends held arrivals to a LOCKED pool through amend_locked_pool with a written reason. The server refuses amendment unless the module is on. Default posture is hold-for-next-cycle.',
    defaultEnabled: true,
    workstream: 'design',
  },
  [FEATURE_FLAGS.STRUCTURE_REORG]: {
    key: FEATURE_FLAGS.STRUCTURE_REORG,
    name: 'Structure Reorganization',
    description:
      'When enabled (and the Structure Reorganization workspace module is on), the hierarchy settings pages offer "Plan a reorganization": a private draft of the structure, a disposition for every unit that leaves it, a worklist of what those units still hold, and one governed authorization. When disabled, the entry point is hidden; the server refuses the reorg RPCs unless the module is on.',
    defaultEnabled: true,
    workstream: 'design',
  },
  [FEATURE_FLAGS.PRODUCT_HIERARCHY]: {
    key: FEATURE_FLAGS.PRODUCT_HIERARCHY,
    name: 'Product Hierarchy',
    description:
      'When enabled (and the Product Hierarchy workspace module is on), Settings → Product Hierarchy exposes the portfolio / product line / product / component tree, and items show a Product placement chip. When disabled, the surface is hidden; product nodes and item placements are retained.',
    defaultEnabled: true,
    workstream: 'design',
  },

  // ============================================
  // OVERLAY WIZARD (migrated from legacy system)
  // ============================================
  [FEATURE_FLAGS.OVERLAY_WIZARD]: {
    key: FEATURE_FLAGS.OVERLAY_WIZARD,
    name: 'Overlay Wizard',
    description: 'Master switch for the floating Overlay Wizard (onboarding/guidance assistant). When disabled, the wizard is not rendered and none of its sub-flags have any effect.',
    defaultEnabled: false,
    workstream: 'design',
    behavioralOnly: true,
  },
  [FEATURE_FLAGS.WIZARD_RECOMMENDATIONS]: {
    key: FEATURE_FLAGS.WIZARD_RECOMMENDATIONS,
    name: 'Wizard Recommendations',
    description: 'When enabled, the Overlay Wizard surfaces context-aware recommendations for the current page. Requires Overlay Wizard.',
    defaultEnabled: false,
    workstream: 'design',
    behavioralOnly: true,
    dependencies: [FEATURE_FLAGS.OVERLAY_WIZARD],
  },
  [FEATURE_FLAGS.WIZARD_TUTORIAL]: {
    key: FEATURE_FLAGS.WIZARD_TUTORIAL,
    name: 'Wizard Tutorial',
    description: 'When enabled, the Overlay Wizard can launch the interactive tutorial flow. Requires Overlay Wizard.',
    defaultEnabled: false,
    workstream: 'design',
    behavioralOnly: true,
    dependencies: [FEATURE_FLAGS.OVERLAY_WIZARD],
  },
  [FEATURE_FLAGS.WIZARD_CONTEXTUAL_GUIDANCE]: {
    key: FEATURE_FLAGS.WIZARD_CONTEXTUAL_GUIDANCE,
    name: 'Wizard Contextual Guidance',
    description: 'When enabled, the Overlay Wizard shows inline contextual guidance tied to the current route. Requires Overlay Wizard.',
    defaultEnabled: false,
    workstream: 'design',
    behavioralOnly: true,
    dependencies: [FEATURE_FLAGS.OVERLAY_WIZARD],
  },

  // ============================================
  // PLATFORM MODULE INDIVIDUAL FLAGS
  // These pair with workspace modules of the same key.
  // Both module (workspace) AND flag (individual) must be enabled.
  // defaultEnabled: true = opt-out model (members have it on by default
  // when the workspace enables the module).
  // ============================================
  [FEATURE_FLAGS.STAKEHOLDER_PULSE]: {
    key: FEATURE_FLAGS.STAKEHOLDER_PULSE,
    name: 'Stakeholder Pulse',
    description: 'Show the Stakeholder Pulse nav item and pulse response panel. Requires the Stakeholder Pulse workspace module to be enabled.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.COST_OF_DELAY]: {
    key: FEATURE_FLAGS.COST_OF_DELAY,
    name: 'Cost of Delay',
    description: 'Show the Impact tab and cost-of-delay tracking on items. Requires the Cost of Delay workspace module to be enabled.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.COMMITMENT_REGISTRY]: {
    key: FEATURE_FLAGS.COMMITMENT_REGISTRY,
    name: 'Commitment Registry',
    description: 'Show the Commitments nav item and commitments section on items. Requires the Commitment Registry workspace module to be enabled.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.STRATEGY_CANVAS_NAV]: {
    key: FEATURE_FLAGS.STRATEGY_CANVAS_NAV,
    name: 'Strategy Canvas',
    description: 'Show the Strategy Canvas nav item in the Strategy section. Requires the Strategy Canvas workspace module to be enabled.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.V2MOM_NAV]: {
    key: FEATURE_FLAGS.V2MOM_NAV,
    name: 'V2MOM',
    description: 'Show the V2MOM nav item in the Strategy section. Requires the V2MOM workspace module to be enabled.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.ELEVATION_NAV]: {
    key: FEATURE_FLAGS.ELEVATION_NAV,
    name: 'Team pages',
    description: "Opens the team hub (/home) and the team pages behind it (Design Language, the Lab, the Method, the Workbook, World, the product roadmap) for the Peak Priorities team. Elevation's own pages open for their owner only. Requires the Team pages workspace module to be enabled.",
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.SESSION_WAITING_ROOM]: {
    key: FEATURE_FLAGS.SESSION_WAITING_ROOM,
    name: 'Session Waiting Room',
    description: 'Adds a pre-session gathering space. Participants land in a Waiting Room phase before the Session Brief; a live roster updates via Realtime as others arrive. Facilitators see a Waiting Room tab in SessionDetail with a Signal Start button that simultaneously advances all waiting participants.',
    defaultEnabled: true,
    workstream: 'ws6',
  },
  [FEATURE_FLAGS.LEXICON]: {
    key: FEATURE_FLAGS.LEXICON,
    name: 'Lexicon',
    description: 'Show the Lexicon nav item and language intelligence surfaces. Requires the Lexicon workspace module to be enabled.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.JUDGMENT_INDEX]: {
    key: FEATURE_FLAGS.JUDGMENT_INDEX,
    name: 'Judgment Index',
    description: 'Show the Judgment Index nav item and Hartman profile surfaces. Advisory only — never alters rankings. Requires the Judgment Index workspace module to be enabled.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.JUDGMENT_ANALYTICS]: {
    key: FEATURE_FLAGS.JUDGMENT_ANALYTICS,
    name: 'Judgment Analytics',
    description:
      'Decision-intelligence / judgment-taxonomy surfaces — calibration, non-consensus edge, judgment-lens profiles, and the leader trust map. Advisory only — never alters rankings or authority. Requires the Judgment Analytics workspace module and entitlement.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.METHOD_INTRO]: {
    key: FEATURE_FLAGS.METHOD_INTRO,
    name: 'Method Intro',
    description: 'Show the first-run Method teaser and the guided tour at /method/tour. Requires the Method Intro workspace module to be enabled.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.GROUP_DECISIONS]: {
    key: FEATURE_FLAGS.GROUP_DECISIONS,
    name: 'Group decisions',
    description: 'Show the Group decisions card and home widget, and the way to start a new group decision. Requires the Group decisions workspace module to be enabled.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.DECISION_BRIEF]: {
    key: FEATURE_FLAGS.DECISION_BRIEF,
    name: 'Decision Log',
    description: 'Show the Decision Log in the sidebar and the Lab, and open its pages (/brief). Requires the Decision Log workspace module to be enabled.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.DELIVERY_TRACKING]: {
    key: FEATURE_FLAGS.DELIVERY_TRACKING,
    name: 'Delivery Tracking',
    description: 'Show the Delivery tab on items and the Calibration dashboard under Reports. Requires the Delivery Tracking workspace module to be enabled.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.EXECUTION_RECIPE]: {
    key: FEATURE_FLAGS.EXECUTION_RECIPE,
    name: 'Work Type Approach',
    description: 'Show the work type approach panel on item pre-planning and the Work Types settings page. Requires the Work Type Approach workspace module to be enabled.',
    defaultEnabled: true,
    workstream: 'platform',
  },

  // ── Rating Explanation & Ranking Expression ───────────────────────────────────
  [FEATURE_FLAGS.RATING_EXPLANATION]: {
    key: FEATURE_FLAGS.RATING_EXPLANATION,
    name: 'Rating Explanation',
    description: 'When enabled, participants can attach a short rationale when they rate or rank an item — capturing the "why" behind each vote. Unlocks the ranking expression sub-flags below.',
    defaultEnabled: false,
    workstream: 'ws6',
    behavioralOnly: true,
  },
  [FEATURE_FLAGS.RANKING_EXPRESSION_IMPORTANCE]: {
    key: FEATURE_FLAGS.RANKING_EXPRESSION_IMPORTANCE,
    name: 'Ranking Expression: Importance',
    description: 'When enabled, participants can frame their ranking as "The level of importance this is to me" instead of a generic ordering — making the personal relevance lens explicit alongside the rank. Requires Rating Explanation.',
    defaultEnabled: false,
    workstream: 'ws6',
    behavioralOnly: true,
    dependencies: [FEATURE_FLAGS.RATING_EXPLANATION],
  },
  [FEATURE_FLAGS.RANKING_EXPRESSION_CONFIDENCE]: {
    key: FEATURE_FLAGS.RANKING_EXPRESSION_CONFIDENCE,
    name: 'Ranking Expression: Confidence (in progress)',
    description: 'When enabled, participants can frame their ranking as "My confidence in this ranking" — surfacing stakeholder disagreement signals (e.g. frontline may rate an item low while finance rates it high). Useful for exposing perspective gaps before a ranking session resolves. Requires Rating Explanation.',
    defaultEnabled: false,
    workstream: 'ws6',
    behavioralOnly: true,
    dependencies: [FEATURE_FLAGS.RATING_EXPLANATION],
  },

  // ── Relationship Graph Extensions ──────────────────────────────────────────
  [FEATURE_FLAGS.GRAPH_INSPECTOR]: {
    key: FEATURE_FLAGS.GRAPH_INSPECTOR,
    name: 'Graph: Relationship Inspector',
    description: 'When enabled, clicking a node in the Relationship Graph opens a slide-over inspector showing all edges, group memberships, and lineage events for that item.',
    defaultEnabled: false,
    workstream: 'platform',
    behavioralOnly: true,
  },
  [FEATURE_FLAGS.GRAPH_PATH_ANALYSIS]: {
    key: FEATURE_FLAGS.GRAPH_PATH_ANALYSIS,
    name: 'Graph: Path & Impact Analysis',
    description: 'When enabled, clicking a node highlights the full transitive dependency chain — showing all upstream and downstream items reachable through any relationship type.',
    defaultEnabled: false,
    workstream: 'platform',
    behavioralOnly: true,
  },
  [FEATURE_FLAGS.GRAPH_CLUSTER_RESOLUTION]: {
    key: FEATURE_FLAGS.GRAPH_CLUSTER_RESOLUTION,
    name: 'Graph: Cluster Resolution UI',
    description: 'When enabled, items in candidate cluster groups show a badge in the graph. Clicking it opens a step-by-step wizard to resolve the cluster as duplicate, variant, or dismissed.',
    defaultEnabled: false,
    workstream: 'platform',
    behavioralOnly: true,
  },
  [FEATURE_FLAGS.GRAPH_NEXUS_OVERLAY]: {
    key: FEATURE_FLAGS.GRAPH_NEXUS_OVERLAY,
    name: 'Graph: Nexus Proposals Overlay',
    description: 'When enabled, active Nexus agent relationship proposals appear as ghost edges in the graph with inline accept/dismiss controls.',
    defaultEnabled: false,
    workstream: 'platform',
    behavioralOnly: true,
  },
  [FEATURE_FLAGS.GRAPH_POOL_VIOLATIONS]: {
    key: FEATURE_FLAGS.GRAPH_POOL_VIOLATIONS,
    name: 'Pool Comparability Enforcement',
    description: 'When enabled, named pool-comparability blockers prevent an invalid ranking session from starting. The Relationship Graph diagnostic panel remains available regardless of this enforcement setting.',
    defaultEnabled: false,
    workstream: 'platform',
    behavioralOnly: true,
  },

  [FEATURE_FLAGS.WHITEBOARD_REVEAL]: {
    key: FEATURE_FLAGS.WHITEBOARD_REVEAL,
    name: 'Whiteboard Import Reveal',
    description:
      'After a whiteboard photo import commits, play a full-screen reveal of the imported graph (fog-lift animation, narrated walkthrough, exportable Peak Priorities diagram), and offer sample boards in the import dialog for a no-camera demo. Presentation only — parsing, mapping, and committing are unaffected.',
    defaultEnabled: true,
    workstream: 'platform',
    behavioralOnly: true,
  },

  [FEATURE_FLAGS.WHITEBOARD_MANAGED_KEY]: {
    key: FEATURE_FLAGS.WHITEBOARD_MANAGED_KEY,
    name: 'Whiteboard import on our Claude key',
    description:
      'Whiteboard photo import reads the photo with our own Claude key, for signed-in members, counted against a monthly limit per workspace. Off by default: turn it on for anyone only after the 20-photo accuracy test (npm run eval:whiteboard) passes. The deployment must also set WHITEBOARD_MANAGED_KEY_ENABLED=true and ANTHROPIC_API_KEY on the server.',
    defaultEnabled: false,
    workstream: 'platform',
    behavioralOnly: true,
  },

  [FEATURE_FLAGS.TIME_PHASED_PLANNING]: {
    key: FEATURE_FLAGS.TIME_PHASED_PLANNING,
    name: 'Time-Phased Execution Planning',
    description:
      'When enabled (and the time_phased_planning workspace module is on), planners get the Launch Calendar, supply calendar workbench, portfolio rollup, and counterfactual levers that answer "buildable but not launchable" and shared-team capacity conflicts.',
    defaultEnabled: true,
    workstream: 'design',
  },
  [FEATURE_FLAGS.RESOURCE_ROSTER]: {
    key: FEATURE_FLAGS.RESOURCE_ROSTER,
    name: 'Roster & qualified capacity',
    description:
      'When enabled (and the resource_roster workspace module is on), People & Capacity gains a Roles view mapping people, generic role placeholders and externals to resource types; a scenario can derive its pools from that roster instead of anonymous numbers; and work can declare capability, credential and named-person requirements so the engine answers "who, at what level" instead of "no capacity". When disabled, scenarios plan against anonymous pools exactly as before — every roster row and requirement is retained.',
    defaultEnabled: true,
    workstream: 'design',
  },
  [FEATURE_FLAGS.PLAN_NARRATIVE]: {
    key: FEATURE_FLAGS.PLAN_NARRATIVE,
    name: 'Unified Plan of Record narrative',
    description:
      'When enabled (and the plan_narrative workspace module is on), the plan cycle’s linked narrative record renders as a plan subpage with a summary card on the plan overview, and standalone narrative URLs for cycle-linked records redirect there. When disabled, the standalone narrative instrument renders unchanged. The one-ceremony rule (plan authorization freezes the linked narrative) is server-side and unaffected by this flag.',
    defaultEnabled: true,
    workstream: 'design',
  },

  [FEATURE_FLAGS.CONDITIONS]: {
    key: FEATURE_FLAGS.CONDITIONS,
    name: 'Conditions',
    description:
      'When enabled (and the conditions workspace module is on), a decision or plan commitment can carry typed conditions — assumptions, numeric thresholds, dependencies, external signals — each individually addressable, and surfaces show per-commitment condition health ("remains committed while X, Y and Z hold"). Existing free-text reconsideration conditions are untouched and keep rendering. Nothing trips, reopens or reorders: a breached threshold produces a proposal on the review docket and a human moves the condition. When disabled, capture and the readouts are hidden; recorded conditions are retained and resume when it is re-enabled.',
    defaultEnabled: true,
    workstream: 'design',
  },

  [FEATURE_FLAGS.DECISION_LATENCY]: {
    key: FEATURE_FLAGS.DECISION_LATENCY,
    name: 'Decision latency report',
    description:
      'When enabled (and the decision_latency workspace module is on), /prioritization/decision-latency decomposes decision time into named stages — framing, deliberation, proposal, approval queue, resolution, ratification, authorization — from timestamps the record already carries. Pure read model: no writes, no ceremonies, nothing stored. Stages whose stamps are missing render as labelled gaps rather than as fast stages. When disabled, the route redirects and nothing else changes.',
    defaultEnabled: true,
    workstream: 'design',
  },

  [FEATURE_FLAGS.COMMITMENT_STATES]: {
    key: FEATURE_FLAGS.COMMITMENT_STATES,
    name: 'Backing (announced vs backed)',
    description:
      'When enabled (and the commitment_states workspace module is on), a ratified priority shows whether resources are actually bound to it: backed when active resource claims cover its recipe-derived requirement, announced when they do not. Derived and read-only — no new state is stored, and priority_sets\u2019 proposed \u2192 decided \u2192 ratified \u2192 published ladder is untouched. Where no recipe demand template exists the readout says there is no resource basis rather than implying coverage. When disabled, no badge, rollup or ceremony renders and nothing else changes.',
    defaultEnabled: true,
    workstream: 'design',
  },

  [FEATURE_FLAGS.COMPARISON_SET_VALIDATION]: {
    key: FEATURE_FLAGS.COMPARISON_SET_VALIDATION,
    name: 'Comparison set validation step',
    description:
      'When enabled (and the comparison_set_validation workspace module is on), the S3 criteria step gains a validation panel that puts the detectors already running — Twins cluster identity, Nexus structural proposals, and the seven pool anti-patterns — in front of the room in canon Rule 13 order before criteria are finalized, and records what was decided as an append-only session event. Accept and dismiss stay the existing RPCs; nothing is auto-confirmed, auto-merged or auto-restructured. When disabled, S3 renders as before and every detector keeps running in the surfaces it already had.',
    defaultEnabled: true,
    workstream: 'design',
  },

  [FEATURE_FLAGS.COMMITMENT_VIEW]: {
    key: FEATURE_FLAGS.COMMITMENT_VIEW,
    name: 'Canonical commitment view',
    description:
      'When enabled (and the commitment_view workspace module is on), /planning/plans/:cycleId/commitments/:itemId assembles one read-only page per commitment — what, why, instead of what, at what cost, under what conditions, how it has changed, who — and surfaces link to it. Every quoting surface additionally carries the version it is quoting and a marker when a newer version has been authorized. Pure assembly over append-only records: no new write paths, no edits, nothing stored. When disabled, the route redirects to the plan overview and the markers do not render.',
    defaultEnabled: true,
    workstream: 'design',
  },

  // App Layer — one flag per consumer app (pairs with same-key workspace module)
  [FEATURE_FLAGS.APP_SESSION_IMPORT]: {
    key: FEATURE_FLAGS.APP_SESSION_IMPORT,
    name: 'Record App (session-results import)',
    description:
      'When enabled (and the app_session_import workspace module is on), the Record app at /apps/session-import is available: import externally-run prioritization results (ranked list or full participant×criterion scores) as a completed canonical session, with deterministic item matching in the wizard and a post-import Twins duplicate sweep.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.APP_RANK]: {
    key: FEATURE_FLAGS.APP_RANK,
    name: 'Rank App',
    description:
      'Retired 2026-10-05 (DEP-029): Rank was the Session app under a second name; /apps/rank now opens Session. The flag is kept so nothing that reads it breaks.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.APP_CUT]: {
    key: FEATURE_FLAGS.APP_CUT,
    name: 'Cut App',
    description:
      'When enabled (and the app_cut workspace module is on), the consumer-simple Cut app at /apps/cut is available: S2 cut-line only — order candidates, draw the cut, save the list as an item pool with the cut line in place. C Mode only; no new schema.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.APP_SESSION]: {
    key: FEATURE_FLAGS.APP_SESSION,
    name: 'Session App',
    description:
      'When enabled (and the app_session workspace module is on), the consumer-simple Session app at /apps/session is available: facilitators set up and invite, participants weigh in from any device. All work flows through the canonical session stores and RPCs.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.APP_ITEMS]: {
    key: FEATURE_FLAGS.APP_ITEMS,
    name: 'Items App',
    description:
      'When enabled (and the app_items workspace module is on), the consumer-simple Items app at /apps/items is available: capture, organize, and track items over the canonical items store. No parallel data model.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.APP_VIABILITY]: {
    key: FEATURE_FLAGS.APP_VIABILITY,
    name: 'Viability App',
    description:
      'When enabled (and the app_viability workspace module is on), the consumer-simple Viability app at /apps/viability is available: a guided projection of the planning engine — viability steps first, plus the three launch-planning steps (Participation, Time-Phased, Funding & Costs) when time-phased planning is on.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.APP_FOCUS]: {
    key: FEATURE_FLAGS.APP_FOCUS,
    name: 'Focus App',
    description:
      'When enabled (and the app_focus workspace module is on), the consumer-simple Focus app at /apps/focus is available: the few things this team is committed to right now, plus the standing pool behind them and a short re-focus ritual. Runs on collections + items. C Mode only; no new schema.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.APP_INTAKE]: {
    key: FEATURE_FLAGS.APP_INTAKE,
    name: 'Intake App',
    description:
      'When enabled (and the app_intake workspace module is on), the consumer-simple Intake app at /apps/intake is available: one mouth for ideas: drop, triage, and an honest status for the submitter. Runs on the canonical items + triage stores. C Mode only; no new schema.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.APP_REVIEW]: {
    key: FEATURE_FLAGS.APP_REVIEW,
    name: 'Review App',
    description:
      'When enabled (and the app_review workspace module is on), the consumer-simple Review app at /apps/review is available: the periodic re-look: what entered, left, or moved since the last published plan of record, plus detector findings. C Mode only; no new schema.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.APP_CLASSIFICATION]: {
    key: FEATURE_FLAGS.APP_CLASSIFICATION,
    name: 'Classification App',
    description:
      'When enabled (and the app_classification workspace module is on), the consumer-simple Classification app at /apps/classification is available: clear the category/altitude proposal queue, accept or reject AI proposals, and confirm classification. C Mode only; no new schema.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.APP_RELATIONSHIPS]: {
    key: FEATURE_FLAGS.APP_RELATIONSHIPS,
    name: 'Relationships App',
    description:
      'When enabled (and the app_relationships workspace module is on), the consumer-simple Relationships app at /apps/relationships is available: review AI Nexus relationship suggestions and inspect how items connect. C Mode only; no new schema.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.APP_ALIGN]: {
    key: FEATURE_FLAGS.APP_ALIGN,
    name: 'Align App',
    description:
      'When enabled (and the app_align workspace module is on), the consumer-simple Align app at /apps/align is available: objectives and the work standing behind them, with the gaps called out. Reads the strategy-alignment layer and Compass findings. C Mode only; no new schema.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.APP_ALLOCATE]: {
    key: FEATURE_FLAGS.APP_ALLOCATE,
    name: 'Allocate App',
    description:
      'When enabled (and the app_allocate workspace module is on), the consumer-simple Allocate app at /apps/allocate is available: split a finite envelope across competing asks and show the work — Buy-a-Feature by default, Cost-of-Delay when the split must be defensible. Runs on the canonical session machinery. C Mode only; no new schema.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.APP_OFFSITE]: {
    key: FEATURE_FLAGS.APP_OFFSITE,
    name: 'Offsite App',
    description:
      'When enabled (and the app_offsite workspace module is on), the consumer-simple Offsite app at /apps/offsite is available: collect the decisions an offsite must make, run the room with present mode plus a phone in every hand, and leave with a record of what was decided, what was parked, and who owns what. Wraps a pre-designed event; the B-Mode event designer stays in the full platform. C Mode only; no new schema.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.APP_HIRE]: {
    key: FEATURE_FLAGS.APP_HIRE,
    name: 'Hire App',
    description:
      'When enabled (and the app_hire workspace module is on), the consumer-simple Hire app at /apps/hire is available: a hiring panel scores candidates against agreed criteria independently, before the debrief, so the discussion starts from real judgement rather than whoever spoke first. Authored on the blueprint renderer; runs on the canonical session machinery. C Mode only; no new schema.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.APP_VENDOR_SELECT]: {
    key: FEATURE_FLAGS.APP_VENDOR_SELECT,
    name: 'Vendor Select App',
    description:
      'When enabled (and the app_vendor_select workspace module is on), the consumer-simple Vendor Select app at /apps/vendor-select is available: compare vendors on weighted criteria and keep the scored trail procurement and legal ask for. Authored on the blueprint renderer; runs on the canonical session machinery. C Mode only; no new schema.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.APP_RISK_TRIAGE]: {
    key: FEATURE_FLAGS.APP_RISK_TRIAGE,
    name: 'Risk Triage App',
    description:
      'When enabled (and the app_risk_triage workspace module is on), the consumer-simple Risk Triage app at /apps/risk-triage is available: plot risks by impact against effort to mitigate, draw the line between mitigate-now and watch, and give the top group owners as canonical commitments. Authored on the blueprint renderer. C Mode only; no new schema.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.APP_POST_MORTEM]: {
    key: FEATURE_FLAGS.APP_POST_MORTEM,
    name: 'Post-Mortem App',
    description:
      'When enabled (and the app_post_mortem workspace module is on), the consumer-simple Post-Mortem app at /apps/post-mortem is available: collect contributing factors, let the room dot-vote what mattered without the seniority gradient, and leave with owners on the fixes. Authored on the blueprint renderer. C Mode only; no new schema.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.APP_ASK_THE_ROOM]: {
    key: FEATURE_FLAGS.APP_ASK_THE_ROOM,
    name: 'Ask the Room App',
    description:
      'When enabled (and the app_ask_the_room workspace module is on), the consumer-simple Ask the Room app at /apps/ask-the-room is available: put a question and its options up, and get an anonymous read from a group in about two minutes. Authored on the blueprint renderer. C Mode only; no new schema.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.APP_START]: {
    key: FEATURE_FLAGS.APP_START,
    name: 'Start (intent-first entry)',
    description:
      'When enabled (and the app_start workspace module is on), the intent-first Start screen at /apps/start is available and supersedes the pre-built configuration picker: people say what they are trying to do and land in the matching app flow with a preset applied. See DEP register for the ConfigurationSelector retirement plan.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.APP_DECISIONS]: {
    key: FEATURE_FLAGS.APP_DECISIONS,
    name: 'Decisions App',
    description:
      'When enabled (and the app_decisions workspace module is on), the Decisions app at /apps/decisions is available: browse every memorialized decision and answer "why?" with a link.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.APP_MANDATE]: {
    key: FEATURE_FLAGS.APP_MANDATE,
    name: 'Mandate App',
    description:
      'When enabled (and the app_mandate workspace module is on), the Mandate app at /apps/mandate is available: frame a new role (situation, the outcome you owe, priorities, stakeholders) and keep the record of how you decided. Offered in personal workspaces.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.APP_WHATS_NEXT]: {
    key: FEATURE_FLAGS.APP_WHATS_NEXT,
    name: 'What\'s Next App',
    description:
      'When enabled (and the app_whats_next workspace module is on), the What\'s Next app at /apps/whats-next is available: Morning Boot, a daily ritual on the person\'s own items (purge, tag, rank the top three, timebox the North Star, focus). Offered in personal workspaces.',
    defaultEnabled: true,
    workstream: 'platform',
  },
  [FEATURE_FLAGS.APP_FIRST_100_DAYS]: {
    key: FEATURE_FLAGS.APP_FIRST_100_DAYS,
    name: 'First 100 Days App',
    description:
      'When enabled (and the app_first_100_days workspace module is on), the First 100 Days app at /apps/first-100-days is available: the hundred-day journey on Mandate\'s record (day counter and phases, listening tour, priority discovery, Day 101). Offered in personal workspaces.',
    defaultEnabled: true,
    workstream: 'platform',
  },
};

/**
 * Flags that are actually checked in app code. Used by the Feature Flags settings page
 * to show "wired in app" vs "defined only". Add an entry here when you gate behavior on a flag.
 */
export const FEATURE_FLAG_USAGE: Partial<Record<FeatureFlagValue, { file: string; description: string }>> = {
  [FEATURE_FLAGS.RESOURCE_ROSTER]: {
    file: 'hooks/useResourceRosterEnabled.ts + pages/planning/PlanningPeopleCapacity.tsx + lib/planningRoster.ts + pages/planning/engine/PlanningCapacityView.tsx',
    description:
      'When enabled (with the resource_roster workspace module), People & Capacity shows the Roles view, a scenario may set its capacity source to the roster (pools rolled up from active role assignments, with drill-through to the people behind the number), and work may declare capability / credential / named-resource requirements that narrow the pool per item. When disabled, every scenario plans against anonymous pools and the stored roster is inert.',
  },
  [FEATURE_FLAGS.CONDITIONS]: {
    file: 'hooks/useConditionsEnabled.ts + lib/conditionObjects.ts + lib/conditionObjectsService.ts + components/prioritization/conditions/*',
    description:
      'When enabled (with the conditions workspace module), decisions and plan commitments carry typed condition objects (migration 699) and the condition-health strip renders wherever a commitment is shown. Writes are RPC-only (attach_condition / record_condition_observation / set_condition_status); the ambient review_due job proposes when a reported reading falls outside a stated bound or a live condition goes unevaluated past the cadence. When disabled, capture and readouts are hidden and the stored conditions are inert — no status changes, no findings acted on.',
  },
  [FEATURE_FLAGS.DECISION_LATENCY]: {
    file: 'hooks/useDecisionLatencyEnabled.ts + pages/prioritization/DecisionLatencyReportPage.tsx + lib/decisionLatencyCore.ts + lib/decisionLatencyService.ts',
    description:
      'When enabled (with the decision_latency workspace module), /prioritization/decision-latency renders per-stage decision latency over existing catchball, session, proposal and cycle-report timestamps, with unmeasured segments shown as labelled gaps and out-of-order stamps excluded rather than averaged. When disabled, the route redirects to /prioritization; no data or behaviour changes anywhere else, since the surface only reads.',
  },
  [FEATURE_FLAGS.COMMITMENT_VIEW]: {
    file: 'hooks/useCommitmentViewEnabled.ts + pages/planning/CommitmentViewPage.tsx + components/planning/CommitmentStaleMarker.tsx + lib/commitmentViewCore.ts + lib/commitmentViewService.ts',
    description:
      'When enabled (with the commitment_view workspace module), /planning/plans/:cycleId/commitments/:itemId renders the canonical commitment view assembled from the decision record, the structural alternatives, resource claims, commitment terms and the plan-version history, and surfaces quoting a commitment show its version plus a staleness marker when a newer version has been authorized. Read-only assembly — no writes, no ceremonies. When disabled, the route redirects to the plan overview and the markers are not rendered; no data changes.',
  },
  [FEATURE_FLAGS.COMMITMENT_STATES]: {
    file: 'hooks/useCommitmentStatesEnabled.ts + lib/commitmentState.ts + lib/commitmentStateService.ts + pages/planning/engine/AboveBelowLineView.tsx + components/plan-of-record/CommitmentStateRollup.tsx',
    description:
      'When enabled (with the commitment_states workspace module), the above/below-line view badges each priority announced or backed and counts them in its header, and the Plan of Record dashboard carries the portfolio rollup (\u201cN ratified \u00b7 N backed \u00b7 N announced only\u201d) plus the announced-only list. Coverage is computed from plan_resource_claims against the recipe-derived requirement; the follow-through view\u2019s hours leg is deliberately not used because nothing writes plan_records.allocated_hours. When disabled, both surfaces render exactly as before.',
  },
  [FEATURE_FLAGS.COMPARISON_SET_VALIDATION]: {
    file: 'hooks/useComparisonSetValidationEnabled.ts + features/session-engine-v3/components/ComparisonSetValidationPanel.tsx + lib/structuralValidationCore.ts + lib/structuralValidationService.ts',
    description:
      'When enabled (with the comparison_set_validation workspace module), the S3 criteria panel renders the structural moment: unresolved Twins clusters first (Rule 13 — identity gates everything else), then Nexus proposals whose both ends are in the pool, then the anti-pattern violations, each resolvable by the existing accept/dismiss RPCs or by a recorded human override. Recording writes one `comparison_set_validated` session event. When disabled, none of it renders and no behaviour elsewhere changes.',
  },
  [FEATURE_FLAGS.PLAN_NARRATIVE]: {
    file: 'hooks/usePlanNarrativeEnabled.ts + pages/planning/PlanNarrativePage.tsx + pages/plan-of-record/PorWorkspace.tsx + pages/planning/PlanningPlanDetail.tsx',
    description:
      'When enabled (with the plan_narrative workspace module), /planning/plans/:cycleId/narrative renders the cycle’s linked narrative record, the plan overview shows the narrative summary card, and standalone narrative URLs for cycle-linked records redirect to the subpage. When disabled, the standalone narrative instrument and the catalog Related card render unchanged.',
  },
  [FEATURE_FLAGS.SESSION_WAITING_ROOM]: {
    file: 'pages/prioritization/sessions/SessionDetail.tsx + pages/participant/ParticipantSessionView.tsx',
    description: 'When enabled, participants land in a Waiting Room phase before the Session Brief (live roster via Realtime on session_presence), and facilitators get a Waiting Room tab in SessionDetail with a Signal Start button that advances all waiting participants at once. When disabled, sessions skip the waiting room and open directly to the brief.',
  },
  [FEATURE_FLAGS.STRATEGY_NAV]: {
    file: 'components/prioritization/Sidebar.tsx',
    description: 'When enabled, shows the Strategy section in the sidebar and strategy alignment fields in item creation. When disabled, the Strategy nav group and strategy fields are hidden.',
  },
  [FEATURE_FLAGS.STRATEGY_KERNEL_NAV]: {
    file: 'components/prioritization/Sidebar.tsx',
    description: 'When enabled, shows the Strategy Kernel entry in the Strategy section. Requires Strategy Navigation to also be enabled. Controlled by the Strategy Kernel workspace module.',
  },
  [FEATURE_FLAGS.PLANNING_NAV]: {
    file: 'components/prioritization/Sidebar.tsx',
    description: 'When enabled, shows the Plans of Record nav item. When disabled, the Plans of Record entry is hidden from the sidebar.',
  },
  [FEATURE_FLAGS.EVIDENCE_ENFORCEMENT]: {
    file: 'stores/prioritization/valueProposalStore.ts',
    description: 'When enabled, value proposals are blocked or warned when the attribute requires evidence and the item does not meet the minimum evidence count. When disabled, proposals can be submitted without meeting evidence requirements.',
  },
  [FEATURE_FLAGS.CYCLE_READINESS]: {
    file: 'lib/cycleReadiness.ts',
    description: 'When enabled, cycle readiness is validated before phase transitions — checks that required setup (participants, workflow, items) is complete. When disabled, readiness validation is skipped and transitions always pass.',
  },
  [FEATURE_FLAGS.GOOD_ITEM_TEST]: {
    file: 'lib/goodItemTest.ts',
    description: 'When enabled, items are evaluated against a 6-check quality test (clear problem, measurable outcome, scoped, etc.) and a quality score is returned. When disabled, the test always returns passed with a perfect score.',
  },
  [FEATURE_FLAGS.PLAN_DRIFT]: {
    file: 'lib/planDrift.ts',
    description: 'When enabled, plan versions are compared against their source Published Priority Lists to detect sequencing divergence. When disabled, drift detection is skipped and no divergence artifacts are produced.',
  },
  [FEATURE_FLAGS.SUBJECT_TYPES]: {
    file: 'components/prioritization/item-view/ItemDetailEditable.tsx',
    description: 'When enabled, shows the Subject Type collapsible field on the item detail page and item work questionnaire view. When disabled, the field is hidden entirely.',
  },
  [FEATURE_FLAGS.DESIGN_HUB]: {
    file: 'components/prioritization/Sidebar.tsx',
    description: 'When enabled, shows the Design Hub nav item in the Processes section. When disabled, the nav item is hidden.',
  },
  [FEATURE_FLAGS.BACKLOG_HEALTH]: {
    file: 'pages/prioritization/PrioritizationApp.tsx + settings/settingsConfig.tsx',
    description: 'When enabled, the Backlog Health settings page is accessible and appears in the Content & Structure settings group. When disabled, the route returns a redirect and the nav entry is hidden.',
  },
  [FEATURE_FLAGS.CYCLE_DESIGNER_V2]: {
    file: 'pages/prioritization/PrioritizationApp.tsx',
    description: 'When enabled, /prioritization/cycles/:cycleId redirects to the native Cycle Designer wizard until launch completes.',
  },
  [FEATURE_FLAGS.TRACK_WORKSPACE_V2]: {
    file: 'pages/prioritization/PrioritizationApp.tsx',
    description:
      'When enabled, /prioritization/tracks/:trackId renders Track Workspace V2 (TrackWorkspace.tsx) in place of the traditional TrackDetail page.',
  },
  [FEATURE_FLAGS.SESSION_ENGINE_V3]: {
    file: 'pages/prioritization/PrioritizationApp.tsx + features/session-engine-v3/',
    description:
      'When enabled, /prioritization/sessions/explore renders the Event Designer. When disabled, /explore redirects to /prioritization/sessions. /sessions/new always mounts the Event Designer and is not gated.',
  },
  [FEATURE_FLAGS.AGENTS_AS_USERS]: {
    file: 'features/voices/hooks/useAgentsAsUsersEnabled.ts + settings/VoicesPage.tsx + SessionDetail participants',
    description:
      'When enabled (and the agents_as_users workspace module is on), Voices appear in Settings → Agents and async session pickers as context providers. When disabled, Voice UI is hidden. The counted aggregate always excludes Voice responses from vote counts.',
  },
  [FEATURE_FLAGS.STORY_OVERLAY]: {
    file: 'features/story-overlay/hooks/useStoryOverlayEnabled + components/prioritization/tracks/TrackProgress.tsx',
    description:
      'When enabled (and the story_overlay workspace module is on), the track page says why its one thing to do now matters, under the call to action. The progress strip and the one thing to do now are part of the track page and are drawn either way.',
  },
  [FEATURE_FLAGS.VOICES_COUNTED]: {
    file: 'features/voices/hooks/useVoicesCountedEnabled.ts + components/SessionVoicesPanel.tsx + tools/AggregationView.tsx + settings/VoiceDetailPage.tsx',
    description:
      'When enabled (with the agents_as_users module), the session Voices panel shows the facilitator-only Advisory/Counted seat toggle, counted estimator responses join the counted aggregate (badged, with a without-agent-voice comparison toggle in results), and the Voice detail page shows the calibration-ledger accuracy summary. When disabled, all seats behave as advisory Phase 1 — official math is byte-identical to Phase 1 because no counted snapshots are written.',
  },
  [FEATURE_FLAGS.CANON_AWARE_HARRY]: {
    file: 'lib/ai/instructionStack.ts + lib/llmHarryService.ts + components/harry/HarryPanel.tsx',
    description:
      'When enabled (and the canon_aware_harry workspace module is on), Harry uses the layered instruction stack and surface context packet. When disabled, the legacy flat prompt path runs unchanged.',
  },
  [FEATURE_FLAGS.SIGNAL_RESPONSE]: {
    file: 'components/signals/* + components/plan-of-record/ConditionBand.tsx + lib/signalEventsService.ts',
    description:
      'When enabled (and the signal_response workspace module is on), POR condition surfaces render the Signal Framework v2 watch band and unified signal log — capture chips, classify/confirm/dismiss, derived exposure badges; no condition letter is selectable. When disabled, the legacy read-only condition band renders and the signal log UI is hidden. Data layer (migrations 672/673) is live regardless of the flag.',
  },
  [FEATURE_FLAGS.SESSION_COPILOT]: {
    file: 'pages/prioritization/sessions/SessionDetail.tsx + components/prioritization/sessions/SessionCopilotPanel.tsx + lib/llmSessionCopilotService.ts',
    description:
      'When enabled (and the session_copilot workspace module is on), SessionDetail mounts the Session Copilot panel (session packet + instruction stack, text drafts only). When disabled, the panel is not rendered and session pages are byte-identical to the legacy surface.',
  },
  [FEATURE_FLAGS.EXCHANGE_DRAFTING]: {
    file: 'components/prioritization/catchball/ExchangeDraftAssist.tsx + RequestComposerPage.tsx + ProposalFormationDrawer.tsx',
    description:
      'When enabled (and the exchange_drafting workspace module is on), the Request composer and the Proposal Formation drawer show the deterministic Loop Intent ambiguity checklist and a "Draft with AI" affordance that fills local form state (lib/llmExchangeDraftService.ts). When disabled, both surfaces render exactly as before; no send/submit path is touched either way.',
  },
  [FEATURE_FLAGS.AMBIENT_RUNTIME]: {
    file: 'features/ambient-runtime/hooks/useAmbientRuntimeEnabled.ts + settings/AmbientRuntime.tsx + settingsConfig.tsx',
    description:
      'When enabled (and the ambient_runtime workspace module is on), the Ambient Runtime settings page and its nav entry are visible. When disabled, the surface is hidden; server-side job execution follows the workspace module only.',
  },
  [FEATURE_FLAGS.SEMANTIC_CONNECTORS]: {
    file: 'features/semantic-connectors/hooks/useSemanticConnectorsEnabled.ts + settings/Excursions.tsx + settingsConfig.tsx',
    description:
      'When enabled (and the semantic_connectors workspace module is on), the Excursions settings page and its nav entry are visible. When disabled, the surface is hidden; excursion records, external-id links, and applied item changes are unaffected.',
  },
  [FEATURE_FLAGS.RECORD_ADDRESS]: {
    file: 'features/record-address/hooks/useRecordAddressEnabled.ts + components/plan-of-record/PorAddressPanel.tsx',
    description:
      'When enabled (and the record_address workspace module is on), the share-link panel on the Ratified Record page is active. The module is on unless a workspace admin switches it off; the server no longer re-checks the module (removed by migration 755), and minting is still limited server-side to the owner, sponsor or a workspace admin (create_por_share_token).',
  },
  [FEATURE_FLAGS.QUERY_FED_POOLS]: {
    file: 'features/pools/hooks/useQueryFedPoolsEnabled.ts + components/prioritization/spaces/PoolFeedPanel.tsx + item-list/components/QueryResultActions.tsx',
    description:
      'When enabled (and the query_fed_pools workspace module is on), the pool editor shows the feed panel (bind, diffed refresh, Arrival Bay) and the item list offers "Feed a pool". When disabled, both are hidden; item_pools.feed_query_id and pool_arrivals rows are unaffected.',
  },
  [FEATURE_FLAGS.POOL_AMENDMENT]: {
    file: 'features/pools/hooks/usePoolAmendmentEnabled.ts + components/prioritization/spaces/PoolFeedPanel.tsx',
    description:
      'When enabled (and the pool_amendment workspace module is on), the Arrival Bay shows "Amend into pool". The module is on unless a workspace admin switches it off; the server no longer re-checks the module (removed by migration 755), and amend_locked_pool still requires the cycle owner or a workspace admin.',
  },
  [FEATURE_FLAGS.STRUCTURE_REORG]: {
    file: 'features/hierarchy/hooks/useStructureReorgEnabled.ts + components/hierarchy/StructureReorgWorkspace.tsx',
    description:
      'When enabled (and the structure_reorg workspace module is on), the hierarchy settings pages show "Plan a reorganization". The module is on unless a workspace admin switches it off; the server no longer re-checks the module (removed by migration 755), and internal_structure_reorg_actor still requires a workspace admin.',
  },
  [FEATURE_FLAGS.PRODUCT_HIERARCHY]: {
    file: 'features/hierarchy/hooks/useProductHierarchyEnabled.ts + settings/ProductHierarchy.tsx + ItemDetailPage.tsx + settingsConfig.tsx',
    description:
      'When enabled (and the product_hierarchy workspace module is on), the Product Hierarchy settings page, its nav entry, and the item Product chip are visible. When disabled, they are hidden; product_nodes rows and items.product_node_id are unaffected.',
  },
  [FEATURE_FLAGS.OVERLAY_WIZARD]: {
    file: 'components/OverlayWizard.tsx + App.tsx',
    description: 'When enabled, renders the floating Knowledge Navigator overlay (bottom-right). When disabled, the wizard is not mounted and sub-flags have no effect.',
  },
  [FEATURE_FLAGS.WIZARD_RECOMMENDATIONS]: {
    file: 'components/OverlayWizard.tsx',
    description: 'When enabled, the expanded wizard shows context-aware “Recommended Next Steps” for the current route. Requires Overlay Wizard.',
  },
  [FEATURE_FLAGS.WIZARD_TUTORIAL]: {
    file: 'components/OverlayWizard.tsx',
    description: 'When enabled, tutorial quick actions, recommendation launches, and triggerWizard events open the interactive tutorial overlay. Requires Overlay Wizard.',
  },
  [FEATURE_FLAGS.WIZARD_CONTEXTUAL_GUIDANCE]: {
    file: 'components/OverlayWizard.tsx',
    description: 'When enabled, the expanded wizard shows the route-specific contextual message banner at the top of the panel. Requires Overlay Wizard.',
  },
  [FEATURE_FLAGS.STAKEHOLDER_PULSE]: {
    file: 'components/prioritization/Sidebar.tsx + pages/prioritization/StakeholderPulsePage.tsx + pages/prioritization/MySubmissions.tsx',
    description: 'When enabled (and the Stakeholder Pulse workspace module is on), shows the Stakeholder Pulse nav item and pulse response panel. When disabled, both are hidden for this user even if the workspace module is on.',
  },
  [FEATURE_FLAGS.COST_OF_DELAY]: {
    file: 'components/prioritization/item-view/ItemImpactTab.tsx + item-view/ItemDetailEditable.tsx + pages/prioritization/ItemDetailPage.tsx',
    description: 'When enabled (and the Cost of Delay workspace module is on), shows the Impact tab and cost-of-delay tracking on items. When disabled, the Impact tab is hidden for this user.',
  },
  [FEATURE_FLAGS.COMMITMENT_REGISTRY]: {
    file: 'components/prioritization/Sidebar.tsx + components/prioritization/item-view/ItemCommitmentsSection.tsx',
    description: 'When enabled (and the Commitment Registry workspace module is on), shows the Commitments nav item and commitments section on items. When disabled, both are hidden for this user.',
  },
  [FEATURE_FLAGS.STRATEGY_CANVAS_NAV]: {
    file: 'components/prioritization/Sidebar.tsx + pages/prioritization/strategy/StrategyCanvasList.tsx',
    description: 'When enabled (and the Strategy Canvas workspace module is on), shows the Strategy Canvas nav item in the Strategy section. When disabled, the nav item is hidden for this user.',
  },
  [FEATURE_FLAGS.V2MOM_NAV]: {
    file: 'components/prioritization/Sidebar.tsx + pages/prioritization/strategy/v2mom/V2momList.tsx',
    description: 'When enabled (and the V2MOM workspace module is on), shows the V2MOM nav item in the Strategy section. When disabled, the nav item is hidden for this user.',
  },
  [FEATURE_FLAGS.ELEVATION_NAV]: {
    file: 'components/auth/RequireElevationAccess.tsx + App.tsx',
    description: "When enabled (and the Team pages workspace module is on), the team opens the team hub (/home) and the team pages behind it, and Elevation's owner opens Elevation's own pages as well (lib/elevationAccess.ts). When disabled, all of them redirect to /prioritization/dashboard.",
  },
  [FEATURE_FLAGS.LEXICON]: {
    file: 'components/prioritization/Sidebar.tsx + pages/prioritization/lexicon/*',
    description: 'When enabled (and the Lexicon workspace module is on), shows the Lexicon nav item and language intelligence routes. When disabled, both are hidden for this user.',
  },
  [FEATURE_FLAGS.JUDGMENT_INDEX]: {
    file: 'components/prioritization/Sidebar.tsx + pages/prioritization/insights/JudgmentIndexPage.tsx',
    description: 'When enabled (and the Judgment Index workspace module is on), shows the Judgment Index nav item and profile management surfaces. Advisory only.',
  },
  [FEATURE_FLAGS.METHOD_INTRO]: {
    file: 'pages/method/MethodTour.tsx + pages/method/MethodHome.tsx',
    description: 'When enabled (and the Method Intro workspace module is on), shows the guided tour. No welcome popup opens for anyone (owner ruling 2026-11-03: the "This app is built on a real book" teaser is off for everyone, always); the tour is reached from "Learn the Method". When disabled, /method/tour redirects to /method and the "Take the tour" CTA is hidden — the static /method reference pages are unaffected either way.',
  },
  [FEATURE_FLAGS.GROUP_DECISIONS]: {
    file: 'components/dashboard/GroupDecisionsWidget.tsx + pages/prioritization/settings/HomeSettings.tsx',
    description: 'When enabled (and the Group decisions workspace module is on), shows the Group decisions card on the dashboard, the group_decisions home widget, and the "New group decision" button. When disabled, all of these are hidden. /groups/join/:token and the /groups/:sessionId page it lands on stay reachable so guest join links keep working.',
  },
  [FEATURE_FLAGS.DECISION_BRIEF]: {
    file: 'components/prioritization/Sidebar.tsx + pages/brief/BriefApp.tsx + pages/prioritization/PrioritizationApp.tsx + pages/Lab.tsx',
    description: 'When enabled (and the Decision Log workspace module is on), shows the Decision Log sidebar item and Lab entry and opens /brief, /brief/new, /brief/:id and /prioritization/decisions/brief. When disabled, those routes redirect to /prioritization/decisions. The public /brief/submit/:token and /brief/view/:token links work either way.',
  },
  [FEATURE_FLAGS.JUDGMENT_ANALYTICS]: {
    file: 'components/prioritization/Sidebar.tsx + pages/prioritization/insights/JudgmentAnalyticsPage.tsx',
    description: 'When enabled (and the Judgment Analytics workspace module + entitlement are on), shows the Judgment Analytics nav item and the leader trust map — per-person calibration, non-consensus edge, and insight archetypes. Advisory only.',
  },
  [FEATURE_FLAGS.DELIVERY_TRACKING]: {
    file: 'components/prioritization/Sidebar.tsx + item-view/ItemDeliveryTab.tsx + pages/prioritization/CalibrationDashboard.tsx',
    description: 'When enabled (and the Delivery Tracking workspace module is on), shows the Delivery tab on items and the Calibration dashboard under Reports.',
  },
  [FEATURE_FLAGS.EXECUTION_RECIPE]: {
    file: 'components/prioritization/item-view/ExecutionRecipePanel.tsx + pages/prioritization/settings/WorkTypesSettings.tsx',
    description: 'When enabled (and the Work Type Approach workspace module is on), shows the work type approach panel on item pre-planning and the Work Types settings page.',
  },
  [FEATURE_FLAGS.RATING_EXPLANATION]: {
    file: 'Not yet wired — flag defined, implementation pending',
    description: 'When enabled, the rating/ranking UI exposes a rationale field so participants can explain why they voted as they did. When disabled, no explanation UI appears and votes are recorded without context.',
  },
  [FEATURE_FLAGS.RANKING_EXPRESSION_IMPORTANCE]: {
    file: 'Not yet wired — flag defined, implementation pending',
    description: 'When enabled (and Rating Explanation is on), participants see an "importance to me" mode selector alongside the standard ranking control. When disabled, only the default ranking mode is available.',
  },
  [FEATURE_FLAGS.RANKING_EXPRESSION_CONFIDENCE]: {
    file: 'Not yet wired — flag defined, implementation pending',
    description: 'When enabled (and Rating Explanation is on), participants can tag their ranking with a confidence signal. Surfaces perspective gaps between stakeholder groups (e.g. frontline vs. finance). When disabled, no confidence framing is shown.',
  },
  [FEATURE_FLAGS.GRAPH_INSPECTOR]: {
    file: 'pages/prioritization/RelationshipGraph.tsx',
    description: 'When enabled, clicking any node in the Relationship Graph opens a slide-over inspector panel showing all edges, group memberships, and lineage events. When disabled, clicking still highlights the 1-hop ego-network but shows no inspector.',
  },
  [FEATURE_FLAGS.GRAPH_PATH_ANALYSIS]: {
    file: 'pages/prioritization/RelationshipGraph.tsx',
    description: 'When enabled, node clicks in the Relationship Graph compute the full transitive reachability set (upstream + downstream) via BFS, coloring nodes by hop distance. When disabled, only direct 1-hop neighbors are highlighted.',
  },
  [FEATURE_FLAGS.GRAPH_CLUSTER_RESOLUTION]: {
    file: 'pages/prioritization/RelationshipGraph.tsx',
    description: 'When enabled, items in candidate cluster groups show a warning badge in the graph node card. Clicking the badge opens a multi-step wizard to mark the cluster as duplicate, variant, or dismissed. When disabled, no cluster badge or wizard appears.',
  },
  [FEATURE_FLAGS.GRAPH_NEXUS_OVERLAY]: {
    file: 'pages/prioritization/RelationshipGraph.tsx',
    description: 'When enabled, Nexus agent relationship proposals are loaded and rendered as ghost edges (cyan dashed lines) in the Relationship Graph. Each ghost edge has inline accept/dismiss controls. When disabled, proposals are only visible on the Nexus Report page.',
  },
  [FEATURE_FLAGS.GRAPH_POOL_VIOLATIONS]: {
    file: 'stores/prioritization/sessionStore.ts',
    description: 'When enabled, ranking-session creation (intent=rank_priorities) is hard-gated when a pool has block-disposition anti-patterns — an unresolved duplicate cluster. A partial package and comparing unlike things are warnings, not blockers (canon 02 §8), so they never gate creation. The read-only Relationship Graph diagnostic panel is always available.',
  },
  [FEATURE_FLAGS.WHITEBOARD_REVEAL]: {
    file: 'components/prioritization/item-list/WhiteboardImportModal.tsx',
    description: 'When enabled, a successful whiteboard import ends in the reveal overlay (fog-lift animation over the capture photo, narrated walkthrough, exportable Peak Priorities diagram) and the import dialog offers sample demo boards. When disabled, imports end on the plain summary screen and no demo entry is shown.',
  },
  [FEATURE_FLAGS.WHITEBOARD_MANAGED_KEY]: {
    file: 'lib/aiProviderKeys.ts (isManagedAnthropicEnabled) + lib/whiteboardImportService.ts',
    description: 'When enabled (and VITE_ANTHROPIC_MANAGED=true), a person with no Claude key of their own can import a whiteboard photo on our key; the server checks sign-in, membership and the monthly limit (api/_lib/managedWhiteboard.js, migration 911).',
  },
  [FEATURE_FLAGS.TIME_PHASED_PLANNING]: {
    file: 'features/time-phased-planning/hooks/useTimePhasedPlanningEnabled.ts + pages/planning/LaunchCalendarPage.tsx + pages/planning/engine/TimePhasedWorkbench.tsx',
    description: 'When enabled (and the time_phased_planning workspace module is on), Launch Calendar and the time-phased workbench are available for portfolio capacity and launch-window planning.',
  },
};

// ─── Storage layer ──────────────────────────────────────────────────────────
//
// Flag overrides live in public.user_feature_flags (see migration 342). This
// module keeps an in-memory mirror keyed on the current user id, populated by
// loadUserFlags() on auth resolve and kept live by a Realtime subscription.
//
// Everything below the "auth-gated behavior begins" marker assumes a user may
// or may not be known. isFeatureEnabled() safely falls back to registry
// defaults when no user is loaded.
// ────────────────────────────────────────────────────────────────────────────

/** Public window event for in-tab reactivity (kept for consumer compatibility). */
export const FEATURE_FLAGS_UPDATED_EVENT = 'feature-flags-updated';

/** Emitted on the window when a DB write fails, so UI can surface a toast. */
export const FEATURE_FLAGS_ERROR_EVENT = 'feature-flags-error';

let flagOverrides: Record<string, boolean> = {};
let currentUserId: string | null = null;
let flagsLoaded = false;
let realtimeChannel: RealtimeChannel | null = null;
let loadPromise: Promise<void> | null = null;

function notifyChanged(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(FEATURE_FLAGS_UPDATED_EVENT, { detail: { overrides: { ...flagOverrides } } })
  );
}

function notifyError(flag: FeatureFlagValue | null, error: unknown): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(FEATURE_FLAGS_ERROR_EVENT, { detail: { flag, error } })
  );
}

/** Fetch all DB overrides for a user and mirror them into module-level state. */
async function fetchAndApplyOverrides(userId: string): Promise<void> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('user_feature_flags')
    .select('flag_key, enabled')
    .eq('user_id', userId);

  if (error) {
    console.error('[featureFlags] Failed to load user flags:', error);
    notifyError(null, error);
    return;
  }

  const next: Record<string, boolean> = {};
  for (const row of (data ?? []) as Array<{ flag_key: string; enabled: boolean }>) {
    next[row.flag_key] = row.enabled;
  }
  flagOverrides = next;
  flagsLoaded = true;
  notifyChanged();
}

function subscribeToRealtimeChanges(userId: string): void {
  const supabase = getSupabaseClient();
  // Tear down any existing subscription (e.g. user switch).
  if (realtimeChannel) {
    void supabase.removeChannel(realtimeChannel);
    realtimeChannel = null;
  }
  realtimeChannel = supabase
    .channel(`user-feature-flags:${userId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'user_feature_flags',
        filter: `user_id=eq.${userId}`,
      },
      () => {
        void fetchAndApplyOverrides(userId);
      }
    )
    .subscribe();
}

/**
 * Load all flag overrides for the given user and start a Realtime subscription
 * so remote changes (other devices) flow back into this tab. Safe to call
 * multiple times; concurrent calls share a single in-flight promise.
 *
 * Call this once after auth resolves — e.g. alongside setSentryContext.
 */
export async function loadUserFlags(userId: string): Promise<void> {
  if (!userId) return;
  // Same user, already loaded/loading → reuse.
  if (currentUserId === userId) {
    if (loadPromise) return loadPromise;
    if (flagsLoaded) return;
  }
  // User switched → drop old state before loading new.
  if (currentUserId && currentUserId !== userId) {
    clearUserFlagsState();
  }
  currentUserId = userId;
  loadPromise = (async () => {
    await fetchAndApplyOverrides(userId);
    subscribeToRealtimeChanges(userId);
  })().finally(() => {
    loadPromise = null;
  });
  return loadPromise;
}

/**
 * Clear in-memory flag state and tear down the Realtime channel. Call on sign out.
 */
export function clearUserFlagsState(): void {
  if (realtimeChannel) {
    try {
      void getSupabaseClient().removeChannel(realtimeChannel);
    } catch {
      /* ignore */
    }
    realtimeChannel = null;
  }
  flagOverrides = {};
  flagsLoaded = false;
  currentUserId = null;
  loadPromise = null;
  notifyChanged();
}

/** True when the user has an explicit row in user_feature_flags for this flag. */
export function hasFlagOverride(flag: FeatureFlagValue): boolean {
  return flag in flagOverrides;
}

/** True once DB overrides have been fetched at least once for the current user. */
export function areUserFlagsLoaded(): boolean {
  return flagsLoaded;
}

// ─── Auth-gated behavior begins ─────────────────────────────────────────────
// All writes below require an authenticated user. Reads (isFeatureEnabled) are
// safe to call before auth resolves — they fall back to registry defaults.

async function persistFlagOverride(flag: FeatureFlagValue, enabled: boolean): Promise<void> {
  const supabase = getSupabaseClient();
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  if (userErr || !userData.user) {
    const err = userErr ?? new Error('No authenticated user');
    notifyError(flag, err);
    throw err;
  }
  const userId = userData.user.id;

  const { error } = await supabase
    .from('user_feature_flags')
    .upsert(
      { user_id: userId, flag_key: flag, enabled },
      { onConflict: 'user_id,flag_key' }
    );

  if (error) {
    console.error('[featureFlags] Failed to persist flag override:', error);
    notifyError(flag, error);
    // Do NOT silently fail — surface the error so the UI can show a toast.
    throw error;
  }
}

async function clearFlagOverride(flag: FeatureFlagValue): Promise<void> {
  const supabase = getSupabaseClient();
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  if (userErr || !userData.user) {
    const err = userErr ?? new Error('No authenticated user');
    notifyError(flag, err);
    throw err;
  }
  const { error } = await supabase
    .from('user_feature_flags')
    .delete()
    .eq('user_id', userData.user.id)
    .eq('flag_key', flag);

  if (error) {
    console.error('[featureFlags] Failed to clear flag override:', error);
    notifyError(flag, error);
    throw error;
  }
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Check if a feature flag is enabled.
 *
 * Reads from the in-memory mirror of the user's DB overrides if loaded,
 * otherwise falls back to FLAG_CONFIGS[flag].defaultEnabled. Never throws.
 * Safe to call before auth resolves (e.g. on the login page).
 */
export function isFeatureEnabled(flag: FeatureFlagValue): boolean {
  const config = FLAG_CONFIGS[flag];
  if (!config) {
    console.warn(`Unknown feature flag: ${flag}`);
    return false;
  }

  if (flag in flagOverrides) {
    if (flagOverrides[flag] && config.dependencies) {
      const allDepsEnabled = config.dependencies.every((dep) => isFeatureEnabled(dep));
      if (!allDepsEnabled) {
        console.warn(`Feature ${flag} enabled but dependencies not met`);
        return false;
      }
    }
    return flagOverrides[flag];
  }

  return config.defaultEnabled;
}

/**
 * Enable a feature flag for the current user. Optimistically updates local
 * state so consumers re-render immediately; persists to the DB asynchronously.
 * Rejects if the write fails so callers can surface an error.
 */
export async function enableFeature(flag: FeatureFlagValue): Promise<void> {
  flagOverrides = { ...flagOverrides, [flag]: true };
  notifyChanged();
  await persistFlagOverride(flag, true);
}

/**
 * Disable a feature flag for the current user. Same semantics as enableFeature.
 */
export async function disableFeature(flag: FeatureFlagValue): Promise<void> {
  flagOverrides = { ...flagOverrides, [flag]: false };
  notifyChanged();
  await persistFlagOverride(flag, false);
}

/**
 * Reset a flag to its registry default by deleting the user's override row.
 * Absence of a row means "use FLAG_CONFIGS[flag].defaultEnabled".
 */
export async function resetFeature(flag: FeatureFlagValue): Promise<void> {
  const next = { ...flagOverrides };
  delete next[flag];
  flagOverrides = next;
  notifyChanged();
  await clearFlagOverride(flag);
}

/** Reset all overrides for the current user. */
export async function resetAllFeatures(): Promise<void> {
  const supabase = getSupabaseClient();
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) return;
  flagOverrides = {};
  notifyChanged();
  const { error } = await supabase
    .from('user_feature_flags')
    .delete()
    .eq('user_id', userData.user.id);
  if (error) {
    console.error('[featureFlags] Failed to reset all flags:', error);
    notifyError(null, error);
    throw error;
  }
}

/** Get all feature flags with their current status. */
export function getAllFeatureFlags(): Array<FeatureFlagConfig & { enabled: boolean }> {
  return Object.values(FLAG_CONFIGS).map((config) => ({
    ...config,
    enabled: isFeatureEnabled(config.key),
  }));
}

/** Get feature flags by workstream. */
export function getFeatureFlagsByWorkstream(
  workstream: FeatureFlagConfig['workstream']
): Array<FeatureFlagConfig & { enabled: boolean }> {
  return getAllFeatureFlags().filter((flag) => flag.workstream === workstream);
}

/** Enable every flag in a workstream (issued as parallel DB writes). */
export async function enableWorkstream(workstream: FeatureFlagConfig['workstream']): Promise<void> {
  const flags = Object.values(FLAG_CONFIGS).filter((config) => config.workstream === workstream);
  await Promise.all(flags.map((f) => enableFeature(f.key)));
}

/** Disable every flag in a workstream (issued as parallel DB writes). */
export async function disableWorkstream(workstream: FeatureFlagConfig['workstream']): Promise<void> {
  const flags = Object.values(FLAG_CONFIGS).filter((config) => config.workstream === workstream);
  await Promise.all(flags.map((f) => disableFeature(f.key)));
}

/**
 * React hook for feature flags.
 *
 * Reactive: re-renders whenever the mirror updates, whether from a local
 * toggle in this tab or a Realtime push from another device. Returns the
 * resolved enabled/disabled value (including defaults when no override exists).
 */
export function useFeatureFlag(flag: FeatureFlagValue): boolean {
  const [enabled, setEnabled] = useState(() => isFeatureEnabled(flag));

  useEffect(() => {
    const refresh = () => setEnabled(isFeatureEnabled(flag));
    // Fired by enableFeature / disableFeature / resetFeature, loadUserFlags,
    // clearUserFlagsState, and the Realtime subscription.
    window.addEventListener(FEATURE_FLAGS_UPDATED_EVENT, refresh);
    return () => {
      window.removeEventListener(FEATURE_FLAGS_UPDATED_EVENT, refresh);
    };
  }, [flag]);

  return enabled;
}

// Debug helper - expose to window in development
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).featureFlags = {
    FEATURE_FLAGS,
    isFeatureEnabled,
    enableFeature,
    disableFeature,
    resetFeature,
    resetAllFeatures,
    getAllFeatureFlags,
    enableWorkstream,
    disableWorkstream,
    loadUserFlags,
    clearUserFlagsState,
    areUserFlagsLoaded,
  };
}
